import React from "react";
import { Tooltip as MuiTooltip, Box } from "@mui/material";
import { colors } from "../../theme/colors";
import { Fade } from "@mui/material";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

export const TOOLTIP_PAPER_SX = {
    position: "relative",
    overflow: "hidden",
    maxWidth: "20rem",
    padding: {
        xs: "0.7rem 0.95rem",
        xl: "0.8rem 1rem",
    },
    borderRadius: "16px",
    background: `
        linear-gradient(
            180deg,
            rgba(14,14,16,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.06)",
    color: colors.text.primary,
    fontSize: {
        xs: "0.775rem",
        xl: "0.825rem",
    },
    fontWeight: 500,
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        0 16px 42px rgba(0,0,0,0.52),
        0 0 24px rgba(232,163,61,0.06)
    `,
    letterSpacing: "0.02em",
    transformOrigin: "center",
    "&::before": {
        content: '""',
        position: "absolute",
        left: "10%",
        right: "10%",
        top: 0,
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(255,255,255,0.14),
                transparent
            )
        `,
    },
};

function TooltipGlow() {
    return (
        <MotionBox
            animate={{
                opacity: [0.14, 0.24, 0.14],
                scale: [1, 1.06, 1],
            }}
            transition={{
                repeat: Infinity,
                duration: 5,
                ease: "easeInOut",
            }}
            sx={{
                position: "absolute",
                width: "10rem",
                height: "10rem",
                top: "-4rem",
                right: "-3rem",
                borderRadius: "50%",
                background: `
                    radial-gradient(
                        circle,
                        rgba(232,163,61,0.22),
                        transparent 72%
                    )
                `,
                filter: "blur(34px)",
                pointerEvents: "none",
            }}
        />
    );
}

export function TooltipShell({ children, sx = {} }) {
    return (
        <Box sx={{ ...TOOLTIP_PAPER_SX, ...sx }}>
            <TooltipGlow />
            <Box sx={{ position: "relative", zIndex: 2 }}>{children}</Box>
        </Box>
    );
}

export default function Tooltip({
    children,
    title,
    placement = "top",
    ...muiTooltipProps
}) {
    return (
        <MuiTooltip
            title={
                <MotionBox
                    initial={{
                        opacity: 0,
                        y: 4,
                        scale: 0.96,
                        filter: "blur(6px)",
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        filter: "blur(0px)",
                    }}
                    transition={{
                        duration: 0.22,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <TooltipGlow />
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 500,
                                letterSpacing: "0.04em",
                                lineHeight: 1.4,
                            }}
                        >
                            {title}
                        </Box>
                    </Box>
                </MotionBox>
            }
            placement={placement}
            TransitionComponent={Fade}
            TransitionProps={{
                timeout: 180,
            }}
            enterDelay={140}
            leaveDelay={0}
            arrow={false}
            {...muiTooltipProps}
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 2],
                            },
                        },
                    ],
                },
                tooltip: {
                    sx: TOOLTIP_PAPER_SX,
                },
            }}
        >
            {children}
        </MuiTooltip>
    );
}
